import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Tooltip } from '@shared/components/Tooltip';
import { Menu, MenuVariant, MenuSize } from '@shared/components/Menu';
import type { TabItem, TabToggleProps } from './types';

// Pill chrome: p-1 (4px each side) + gap-1 (4px between children).
const PILL_PADDING = 8;
const GAP = 4;

function TabToggle<T extends string>({
  tabs,
  activeTab,
  onChange,
  ariaLabel,
  className = '',
  fitContent = false,
  overflow = false,
  moreLabel = 'More',
}: TabToggleProps<T>) {
  const tabSizeClass = fitContent ? 'px-[21px]' : 'w-full';
  const wrapperClass = fitContent ? '' : 'flex-1';

  const tabButtonClass = (tab: TabItem<T>, isActive: boolean) =>
    `${tabSizeClass} h-[40px] rounded-md text-titleMedium font-semibold whitespace-nowrap transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] ${
      tab.disabled
        ? 'opacity-38 cursor-not-allowed text-[#474A4C]'
        : isActive
          ? 'bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.15),0px_2px_6px_2px_rgba(0,0,0,0.08)] text-[#2A4455] cursor-pointer'
          : 'text-[#878b8e] hover:text-[#2A4455] cursor-pointer'
    }`;

  const renderTab = (tab: TabItem<T>) => {
    const isActive = activeTab === tab.id;
    const button = (
      <button
        role="tab"
        type="button"
        data-testid={`tab-${tab.id}`}
        aria-selected={isActive}
        disabled={tab.disabled}
        onClick={() => !tab.disabled && onChange(tab.id)}
        className={tabButtonClass(tab, isActive)}
      >
        {tab.label}
      </button>
    );

    return (
      <div key={tab.id} className={wrapperClass}>
        {tab.disabled && tab.disabledTooltip ? (
          <Tooltip
            content={tab.disabledTooltip}
            side="bottom"
            cursor="not-allowed"
            triggerTabIndex={-1}
          >
            {button}
          </Tooltip>
        ) : (
          button
        )}
      </div>
    );
  };

  // ── Non-overflow (original behavior) ──────────────────────────────────────
  if (!overflow) {
    const containerClass = fitContent
      ? 'inline-flex bg-[#ECEDF0] rounded-xl p-1 gap-1 max-w-full h-[48px]'
      : 'flex bg-[#ECEDF0] rounded-xl p-1 gap-1 w-full max-w-[432px] h-[48px]';
    return (
      <div
        role="tablist"
        aria-label={ariaLabel}
        className={`${containerClass} ${className}`}
      >
        {tabs.map(renderTab)}
      </div>
    );
  }

  // ── Overflow ("More" dropdown) mode ───────────────────────────────────────
  return (
    <OverflowTabToggle
      tabs={tabs}
      activeTab={activeTab}
      onChange={onChange}
      ariaLabel={ariaLabel}
      className={className}
      tabSizeClass={tabSizeClass}
      tabButtonClass={tabButtonClass}
      renderTab={renderTab}
      moreLabel={moreLabel}
    />
  );
}

// Split into its own component so the hooks below only run in overflow mode
// (hooks can't be called conditionally in the parent).
interface OverflowProps<T extends string> {
  tabs: TabItem<T>[];
  activeTab: T;
  onChange: (tab: T) => void;
  ariaLabel: string | undefined;
  className: string;
  tabSizeClass: string;
  tabButtonClass: (tab: TabItem<T>, isActive: boolean) => string;
  renderTab: (tab: TabItem<T>) => React.ReactNode;
  moreLabel: string;
}

function OverflowTabToggle<T extends string>({
  tabs,
  activeTab,
  onChange,
  ariaLabel,
  className,
  tabSizeClass,
  tabButtonClass,
  renderTab,
  moreLabel,
}: OverflowProps<T>) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const measureRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const moreMeasureRef = useRef<HTMLButtonElement | null>(null);
  const moreContainerRef = useRef<HTMLDivElement | null>(null);

  const [visibleCount, setVisibleCount] = useState(tabs.length);
  const [menuOpen, setMenuOpen] = useState(false);

  // Re-measure whenever the tab set (ids/labels/disabled) changes, not on every
  // render — labels affect natural width, so they belong in the signature.
  const tabsSignature = tabs
    .map((t) => `${t.id}:${t.label}:${t.disabled ? 1 : 0}`)
    .join('|');

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) {
      return;
    }
    const total = tabs.length;
    const compute = () => {
      // Not laid out yet (or a non-layout environment like jsdom): show every
      // tab rather than collapsing them all into "More".
      if (wrapper.clientWidth <= 0) {
        setVisibleCount(total);
        return;
      }
      const available = wrapper.clientWidth - PILL_PADDING;
      const widths = measureRefs.current
        .slice(0, total)
        .map((el) => el?.offsetWidth ?? 0);
      const totalWidth =
        widths.reduce((sum, w) => sum + w, 0) +
        GAP * Math.max(0, widths.length - 1);
      // Everything fits — show all tabs, no "More".
      if (totalWidth <= available) {
        setVisibleCount(total);
        return;
      }
      // Reserve room for the "More" trigger, then greedily fit tabs left→right.
      const moreWidth = moreMeasureRef.current?.offsetWidth ?? 0;
      let used = moreWidth;
      let count = 0;
      for (let i = 0; i < widths.length; i += 1) {
        const next = (widths[i] ?? 0) + GAP;
        if (used + next <= available) {
          used += next;
          count += 1;
        } else {
          break;
        }
      }
      setVisibleCount(Math.max(1, count));
    };

    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(wrapper);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsSignature]);

  const visibleTabs = tabs.slice(0, visibleCount);
  const overflowTabs = tabs.slice(visibleCount);
  const hasOverflow = overflowTabs.length > 0;
  const moreActive = overflowTabs.some((t) => t.id === activeTab);

  // Nothing overflows anymore (e.g. widened back) — make sure the menu is shut.
  useEffect(() => {
    if (!hasOverflow && menuOpen) {
      setMenuOpen(false);
    }
  }, [hasOverflow, menuOpen]);

  // Close the dropdown on outside click / Escape.
  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const onPointerDown = (e: MouseEvent) => {
      if (
        moreContainerRef.current &&
        !moreContainerRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const moreButtonClass = `${tabSizeClass} inline-flex items-center gap-1 h-[40px] rounded-md text-titleMedium font-semibold whitespace-nowrap transition-all cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] ${
    moreActive
      ? 'bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.15),0px_2px_6px_2px_rgba(0,0,0,0.08)] text-[#2A4455]'
      : 'text-[#878b8e] hover:text-[#2A4455]'
  }`;

  const chevron = (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      className={`shrink-0 transition-transform duration-200 ${menuOpen ? 'rotate-180' : ''}`}
    >
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  return (
    <div ref={wrapperRef} className="w-full min-w-0">
      <div
        role="tablist"
        aria-label={ariaLabel}
        className={`inline-flex bg-[#ECEDF0] rounded-xl p-1 gap-1 max-w-full h-[48px] ${className}`}
      >
        {visibleTabs.map(renderTab)}

        {hasOverflow && (
          <div key="__more__" ref={moreContainerRef} className="relative">
            <button
              type="button"
              data-testid="tab-more"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((prev) => !prev)}
              className={moreButtonClass}
            >
              {moreLabel}
              {chevron}
            </button>

            {menuOpen && (
              <div className="absolute left-0 top-full mt-1 z-50">
                <Menu
                  items={overflowTabs.map((t) => ({
                    id: t.id,
                    label: t.label,
                    disabled: t.disabled ?? false,
                    ...(t.disabledTooltip !== undefined && {
                      disabledTooltip: t.disabledTooltip,
                    }),
                    disabledTooltipSide: 'left' as const,
                  }))}
                  variant={MenuVariant.Light}
                  size={MenuSize.Medium}
                  width={220}
                  selectedId={activeTab}
                  toolTip={false}
                  showLeadingIcons={false}
                  onItemClick={(id) => {
                    setMenuOpen(false);
                    onChange(id as T);
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Hidden measurement layer: every tab + the "More" trigger at natural
          size. Absolutely positioned so it never affects layout; used only to
          read offsetWidth for the overflow calculation. */}
      <div
        aria-hidden
        className="absolute -left-[9999px] top-0 invisible inline-flex gap-1 p-1"
      >
        {tabs.map((tab, i) => (
          <button
            key={`measure-${tab.id}`}
            ref={(el) => {
              measureRefs.current[i] = el;
            }}
            type="button"
            tabIndex={-1}
            className={tabButtonClass(tab, false)}
          >
            {tab.label}
          </button>
        ))}
        <button
          ref={moreMeasureRef}
          type="button"
          tabIndex={-1}
          className={moreButtonClass}
        >
          {moreLabel}
          {chevron}
        </button>
      </div>
    </div>
  );
}

export default TabToggle;
